// src/pages/main.tsx

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Edit,
  Users,
  AlignVerticalDistributeCenter,
  Crown,
  GitBranch,
  Clock,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";

import ServiceCard from "../components/ServiceCard";

interface ServiceItem {
  key: string;
  title: string;
  description: string;
  path: string;
  icon: React.ReactElement;
}

const Container = styled.div`
  background: linear-gradient(135deg, #f5f7fa 0%, #e4edf5 100%);
  color: #2d3748;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 60px;

  h1 {
    font-size: 2.8rem;
    font-weight: 700;
    background: linear-gradient(90deg, #4a90e2, #9010d8);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 0.5px;
  }

  p {
    color: #718096;
    font-size: 1.1rem;
    margin-top: 12px;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  direction: rtl;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 28px;
  width: 100%;
  max-width: 1280px;
`;

const ExternalLinkCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(74, 144, 226, 0.15);
  }
`;

const ExternalLinkButton = styled.a`
  display: inline-block;
  margin-top: 24px;
  padding: 14px 32px;
  background: linear-gradient(135deg, #4a90e2, #357abd);
  color: white;
  border: none;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
  font-size: 1.05rem;
  box-shadow: 0 4px 15px rgba(74, 144, 226, 0.3);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(74, 144, 226, 0.4);
  }
`;

const Main: React.FC = () => {
  const [role, setRole] = useState<string>("admin");

  useEffect(() => {
    try {
      const userItem = localStorage.getItem("user");
      if (userItem) {
        const user = JSON.parse(userItem);
        if (user?.roles?.length > 0) {
          setRole(user.roles[0]);
        }
      }
    } catch (error) {
      console.error("Failed to parse user from localStorage:", error);
    }
  }, []);

  const virusDetectionTitle = "مشاهده جوامع استخراج شده";
  const isLimitedRole = role === "fars";

  const services: ServiceItem[] = [
    {
      key: "1",
      title: "بررسی و پیشنهاد جمله",
      description:
        "تحلیل و بهبود جملات برای حداکثر تأثیرگذاری در شبکه‌های اجتماعی.",
      path: "/EntryModificationPhrase",
      icon: <Edit size={28} />,
    },
    {
      key: "2",
      title: "مشاهده جوامع استخراج شده",
      description: "کشف و تحلیل جوامع و گروه‌های کلیدی در شبکه‌های ارتباطی.",
      path: "/CommunityDetection",
      icon: <Users size={28} />,
    },
    {
      key: "3",
      title: "شناسایی ویروسی شدن پیام",
      description: "پیش‌بینی پتانسیل ویروسی شدن یک پیام قبل از انتشار گسترده.",
      path: "/VirusDetection",
      icon: <AlignVerticalDistributeCenter size={28} />,
    },
    {
      key: "4",
      title: "شناسایی رهبران عقیده",
      description: "یافتن افراد تأثیرگذار و رهبران افکار عمومی در یک جامعه.",
      path: "/LeaderDetection",
      icon: <Crown size={28} />,
    },
    {
      key: "5",
      title: "پیش‌بینی انتشار پیام",
      description: "مدل‌سازی و پیش‌بینی مسیر و سرعت انتشار یک پیام در شبکه.",
      path: "/treePrediction",
      icon: <GitBranch size={28} />,
    },
    {
      key: "6",
      title: "پیشنهاد بهترین فرد و زمان",
      description: "بهینه‌سازی استراتژی انتشار با پیشنهاد بهترین فرد و زمان.",
      path: "/publisherSuggestion",
      icon: <Clock size={28} />,
    },
    {
      key: "7",
      title: "پیش‌بینی شایعه",
      description:
        "تشخیص و تحلیل شایعات برای جلوگیری از انتشار اطلاعات نادرست.",
      path: "/RumorDetection",
      icon: <AlertTriangle size={28} />,
    },
  ];

  return (
    <Container>
      <Header>
        <h1>سامانه انتشار پیام سمنتا</h1>
        <p>
          ابزارهای هوشمند تحلیل و بهینه‌سازی انتشار پیام در شبکه‌های اجتماعی
        </p>
      </Header>

      <ServicesGrid>
        {services.map((service) => {
          const isVirusCard = service.title === virusDetectionTitle;
          const disabled = isLimitedRole && !isVirusCard;

          return (
            <ServiceCard
              key={service.key}
              title={service.title}
              description={service.description}
              path={service.path}
              icon={service.icon}
              disabled={disabled}
            />
          );
        })}

        <ExternalLinkCard>
          <TrendingUp size={56} color="#4a90e2" strokeWidth={2.5} />
          <h3
            style={{
              margin: "20px 0 12px",
              fontSize: "1.4rem",
              fontWeight: 700,
              color: "#2d3748",
            }}
          >
            مشاهده گراف و نمودارها
          </h3>
          <p style={{ color: "#718096", lineHeight: "1.6", maxWidth: "280px" }}>
            بررسی بصری داده‌های انتشار، شبکه‌ها و تحلیل‌های پیشرفته به صورت
            تعاملی
          </p>
          <ExternalLinkButton
            href="https://contagion.100gan.ir/dashboard-page"
            target="_blank"
            rel="noopener noreferrer"
          >
            ورود به داشبورد
          </ExternalLinkButton>
        </ExternalLinkCard>
      </ServicesGrid>
    </Container>
  );
};

export default Main;
